import axios from 'axios';
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { SpinnerLoader } from './components/Spinner';
import { TransactionDetail } from './components/TransactionDetail';
import { PaymentFormLayout } from './components/PaymentFormLayout';
import { directSaleTenant } from './pixelpay/pixelpay';
import { NotFound } from './components/NotFound';
import { ServiceClosed } from './components/ServiceClosed';
import states from '@pixelpay/sdk-core/lib/assets/states.json'
import phoneCode from '@pixelpay/sdk-core/lib/assets/formats.json'

const baseUrl = process.env.REACT_APP_API_URL
const procotol = process.env.REACT_APP_API_PROTOCOL

// Marca de tarjeta a partir del prefijo (dato NO sensible)
const detectCardBrand = (n) => {
    const s = (n || '').replace(/\s/g, '')
    if (/^4/.test(s)) return 'VISA'
    if (/^(5[1-5]|2[2-7])/.test(s)) return 'MASTERCARD'
    if (/^3[47]/.test(s)) return 'AMEX'
    if (/^6/.test(s)) return 'DISCOVER'
    return ''
}

// Reporta el resultado del intento de pago a la bitácora (best-effort: nunca rompe el flujo).
// Solo datos NO sensibles: nunca el número completo ni el CVV.
const reportTransactionLog = async (payload) => {
    try {
        await axios.post(`${procotol}//${baseUrl}/administration/api/register-transaction-log/`, payload)
    } catch (e) {
        // El logueo es secundario; si falla no afecta al usuario.
    }
}

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY

const loadRecaptchaScript = () => {
    if (!RECAPTCHA_SITE_KEY) return
    if (document.querySelector('script[data-recaptcha]')) return
    const s = document.createElement('script')
    s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`
    s.async = true
    s.defer = true
    s.setAttribute('data-recaptcha', '1')
    document.body.appendChild(s)
}

const getRecaptchaToken = async () => {
    try {
        if (RECAPTCHA_SITE_KEY && window.grecaptcha) {
            await new Promise((resolve) => window.grecaptcha.ready(resolve))
            return await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'payment' })
        }
    } catch (e) {
        // Si reCAPTCHA falla, no bloqueamos desde el front; el backend valida.
    }
    return ''
}

export const PymePaymentLink = () => {

    const { tenant, uuid } = useParams();
    const [stateSelect, setStateSelect] = useState({})
    const [phoneCodeSelected, setPhoneCodeSelected] = useState(null)
    const [isInternational, setIsInternational] = useState(false);
    const [showBillingAddress, setShowBillingAddress] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [validatePyme, setValidatePyme] = useState(null);
    const [detailData, setDetailData] = useState(null)
    const [showTransactionDetail, setShowTransactionDetail] = useState(false)
    const [trasactionDetailData, setTrasactionDetailData] = useState(null)
    const [isPosting, setIsPosting] = useState(false);
    const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm();
    watch(["country"])
    const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    const currentYear = new Date().getFullYear()
    const maxYear = currentYear + 20;
    let years = [];

    for (let i = currentYear; i <= maxYear; i++) {
        years.push(i);
    }

    useEffect(() => {
        async function fnValidatePyme(tenant, uuid) {
            try {
                setIsLoading(true);
                const url = `${procotol}//${baseUrl}/pyme/${tenant}/console/api/validate-payment-link/${uuid}/`;
                const res = await axios.get(url);
                setValidatePyme(res.data)
                setValue('amount', res.data.link.amount)
            } catch (err) {
                const { response } = err
                setValidatePyme(response.data)
            }
            finally {
                setIsLoading(false)
            }
        }
        fnValidatePyme(tenant, uuid)
    }, [tenant, uuid, setValue])

    useEffect(() => {
        loadRecaptchaScript()
    }, [])


    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (name === 'country') {
                if (!!value.country) {
                    setStateSelect(states[value.country])
                    setPhoneCodeSelected(`+${phoneCode[value.country].phone_code}`);
                } else {
                    setStateSelect({})
                    setPhoneCodeSelected(null)
                }
            }
        })

        return () => subscription.unsubscribe()
    }, [watch])

    // Cuando cambie a internacional, activar checkbox de facturación
    useEffect(() => {
        if (isInternational) {
            setShowBillingAddress(true);
        }
    }, [isInternational])


    const onSubmit = async (data) => {
        try {
            setIsPosting(true);
            data = {
                ...data,
                number: data.number.replaceAll(' ', '')
            }
            let startTranscData = {
                email: data.email,
                cardholder: data.cardholder,
                amount: data.amount,
            }

            if (!isInternational && !showBillingAddress) {
                data.country = "HN";
                data.state = "HN-FM";
                data.city = "Tegucigalpa";
                data.address = "Tegucigalpa";
                data.phone = "94043476";
                data.zip = "11101";
            }


            // reCAPTCHA + credenciales (Modelo C): este flujo no tiene start server-side,
            // así que el backend firma la orden dentro de la verificación de reCAPTCHA.
            const recaptchaToken = await getRecaptchaToken();
            const correlative = validatePyme.link.correlative;
            const resVerify = await axios.post(`${procotol}//${baseUrl}/administration/api/verify-recaptcha/`, { recaptcha_token: recaptchaToken, order_id: correlative });
            const credentials = resVerify.data.credentials;

            const urlEmails = `${procotol}//${baseUrl}/pyme/${tenant}/console/api/email-queue/`;
            const resEmail = await axios.get(urlEmails);
            const res = await directSaleTenant(data, correlative, resEmail.data.email, validatePyme.pyme_name, credentials)
            const success = !!(res && res.success);

            // Bitácora (no sensible, best-effort)
            reportTransactionLog({
                source: 'pyme',
                reference: tenant,
                correlative,
                amount: data.amount,
                card_last4: (data.number || '').slice(-4),
                card_brand: detectCardBrand(data.number),
                cardholder: data.cardholder,
                email: data.email,
                result: success ? 'success' : 'error',
                error_code: res?.code ? `${res.code}` : '',
                error_message: success ? '' : (res?.message || 'Fallo en el procesamiento del pago'),
                raw_response: res || null,
            });

            if (success) {
                const url = `${procotol}//${baseUrl}/pyme/${tenant}/console/api/register-transaction/${uuid}/`;
                const postData = {
                    payment_response: res.data,
                    customer_data: {
                        name: data.cardholder,
                        phone: data.phone,
                        email: data.email
                    }
                }

                await axios.post(url, postData);
                setTrasactionDetailData(res)
                setDetailData({
                    correlative: correlative,
                    number: data.number.slice(-4),
                    ...startTranscData,
                })
                setShowTransactionDetail(true)
            } else {
                Swal.fire(
                    'Algo no salió bien!',
                    `${res?.message || 'No se pudo procesar el pago.'}`,
                    'error'
                )
            }
        } catch (err) {
            const { response } = err
            Swal.fire(
                'Algo no salió bien!',
                `${response?.data?.msg}`,
                'error'
            )
        }
        finally {
            setIsPosting(false)
        }
    }

    if (isLoading) {
        return <SpinnerLoader />
    }
    if (validatePyme?.status === 'closed') {
        return <ServiceClosed data={validatePyme} />
    }
    if (validatePyme?.status === 'error') {
        return <NotFound />
    }

    // Determinar si mostrar los campos adicionales
    const showAdditionalFields = isInternational || showBillingAddress;

    if (showTransactionDetail) {
        return (
            <TransactionDetail
                data={trasactionDetailData}
                entrepreneur={validatePyme}
                detailData={detailData}
            />
        )
    }

    return (
        <PaymentFormLayout
            logoUrl={validatePyme?.pyme_logo ? `${procotol}//${baseUrl}/${validatePyme.pyme_logo}` : null}
            title={validatePyme?.pyme_name}
            description={validatePyme?.link?.description}
            register={register}
            errors={errors}
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            isInternational={isInternational}
            setIsInternational={setIsInternational}
            showBillingAddress={showBillingAddress}
            setShowBillingAddress={setShowBillingAddress}
            showAdditionalFields={showAdditionalFields}
            stateSelect={stateSelect}
            phoneCodeSelected={phoneCodeSelected}
            months={months}
            years={years}
            isPosting={isPosting}
            amountReadOnly={true}
            amountValue={validatePyme?.link?.amount}
        />
    )
}
